// ─────────────────────────────────────────────────────────────────────────────
// InspectionHierarchyPage.jsx
// Denetim hiyerarşisinin tamamını yönetir:
//   Denetim Yeri → Form Tipi → Kategori → Soru → Uygunsuzluk → Tehlike → Risk
//   + Yasal Prosedürler (ayrı kart)
//
// Her seviyede: liste, ekle, düzenle, soft-delete.
// "Form Tipi" satırında FormBuilder'a bağlantı butonu var.
// categories & questions seviyelerinde: sürükle-bırak + ↑↓ yön tuşları ile reorder.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Card, CardBody, Table, Badge, Button,
  Modal, ModalHeader, ModalBody, ModalFooter,
  FormGroup, Label, Input, Alert, Spinner,
  Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from 'reactstrap';
import {
  Plus, Edit2, Trash2, ChevronRight, ChevronLeft,
  ExternalLink, BookOpen, X, Camera, ChevronDown, Lightbulb,
  GripVertical, ChevronUp, Copy, ListTree,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import * as adminSvc from '../../../services/inspectionAdminService';
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

// ── Level Konfigürasyonu ───────────────────────────────────────────────────────
const LEVEL_CONFIG = [
  {
    key: 'departments', get label() { return i18n.t("formlar:inspectionHierarchy.birim"); }, get plural() { return i18n.t("formlar:inspectionHierarchy.birimler"); },
    idField: 'inspection_department_id', nameField: 'inspection_department_name',
    color: 'success',
    fields: [
      { key: 'name',        dataKey: 'inspection_department_name',        get label() { return i18n.t("formlar:inspectionHierarchy.birimAdi"); }, type: 'text', required: true },
      { key: 'description', dataKey: 'inspection_department_description', get label() { return i18n.t("formlar:inspectionHierarchy.aciklama"); },  type: 'text' },
    ],
  },
  {
    key: 'scopes',   get label() { return i18n.t("formlar:inspectionHierarchy.denetimYeri"); },  get plural() { return i18n.t("formlar:inspectionHierarchy.denetimYerleri"); },
    idField: 'inspection_scope_id',   nameField: 'inspection_scope_name',
    color: 'primary',
    fields: [
      { key: 'name',         dataKey: 'inspection_scope_name',        get label() { return i18n.t("formlar:inspectionHierarchy.denetimYeriAdi"); }, type: 'text',   required: true },
      { key: 'description',  dataKey: 'inspection_scope_description', get label() { return i18n.t("formlar:inspectionHierarchy.aciklama"); },      type: 'text' },
      { key: 'targetEntity', dataKey: 'target_entity',                get label() { return i18n.t("formlar:inspectionHierarchy.hedefVarlik"); },  type: 'select',
        options: [
          { value: '',           get label() { return i18n.t("formlar:inspectionHierarchy.secin"); }  },
          { value: 'stand',      get label() { return i18n.t("formlar:inspectionHierarchy.stand"); }       },
          { value: 'salon',      get label() { return i18n.t("formlar:inspectionHierarchy.salon"); }       },
          { value: 'fuar_alani', get label() { return i18n.t("formlar:inspectionHierarchy.fuarAlani"); } },
          { value: 'fuar',       get label() { return i18n.t("formlar:inspectionHierarchy.fuar"); }        },
        ],
      },
      { key: 'parentId', dataKey: 'inspection_department_id', get label() { return i18n.t("formlar:inspectionHierarchy.birim"); }, type: 'select',
        numeric: true, options: [], optionsFrom: 'departments' , hidden: true },
    ],
  },
  {
    key: 'types',    get label() { return i18n.t("formlar:inspectionHierarchy.formTipi"); }, get plural() { return i18n.t("formlar:inspectionHierarchy.formTipleri"); },
    idField: 'inspection_type_id',    nameField: 'inspection_type_name',
    color: 'info', hasFormBuilder: true,
    fields: [
      { key: 'name',                  dataKey: 'inspection_type_name',     get label() { return i18n.t("formlar:inspectionHierarchy.formAdi"); },         type: 'text', required: true },
      { key: 'shortName',             dataKey: 'inspection_type_short',    get label() { return i18n.t("formlar:inspectionHierarchy.kisaKodMaks15"); },type: 'text', maxLength: 15 },
      { key: 'mainQuestion',          dataKey: 'main_question',            get label() { return i18n.t("formlar:inspectionHierarchy.anaSoru"); },          type: 'text' },
      { key: 'mainQuestionQuestType', dataKey: 'main_question_quest_type', get label() { return i18n.t("formlar:inspectionHierarchy.cevapTipi"); },       type: 'select',
        options: [
          { value: '',  get label() { return i18n.t("formlar:inspectionHierarchy.secin"); }          },
          { value: 1, get label() { return i18n.t("formlar:inspectionHierarchy.uygunUygunDegil"); } },
          { value: 2, get label() { return i18n.t("formlar:inspectionHierarchy.evetHayir"); }        },
          { value: 3, get label() { return i18n.t("formlar:inspectionHierarchy.varYok"); }           },
          { value: 4, get label() { return i18n.t("formlar:inspectionHierarchy.problemVarYok"); }   },
        ],
      },
    ],
  },
  {
    key: 'categories', get label() { return i18n.t("formlar:inspectionHierarchy.kategori"); }, get plural() { return i18n.t("formlar:inspectionHierarchy.kategoriler"); },
    idField: 'inspection_category_id', nameField: 'inspection_category_name',
    color: 'secondary',
    fields: [
      { key: 'name',        dataKey: 'inspection_category_name',        get label() { return i18n.t("formlar:inspectionHierarchy.kategoriAdi"); }, type: 'text', required: true },
      { key: 'description', dataKey: 'inspection_category_description', get label() { return i18n.t("formlar:inspectionHierarchy.aciklama"); },      type: 'text' },
    ],
  },
  {
    key: 'questions', get label() { return i18n.t("formlar:inspectionHierarchy.soru"); }, get plural() { return i18n.t("formlar:inspectionHierarchy.sorular"); },
    idField: 'inspection_question_id', nameField: 'inspection_question_name',
    color: 'dark',
    fields: [
      { key: 'name',            dataKey: 'inspection_question_name',        get label() { return i18n.t("formlar:inspectionHierarchy.soruMetni"); },       type: 'text', required: true },
      { key: 'description',     dataKey: 'inspection_question_description', get label() { return i18n.t("formlar:inspectionHierarchy.aciklama"); },          type: 'text' },
      { key: 'questType',       dataKey: 'quest_type',                      get label() { return i18n.t("formlar:inspectionHierarchy.cevapTipi"); },        type: 'select',
        options: [
          { value: '',  get label() { return i18n.t("formlar:inspectionHierarchy.secin"); }          },
          { value: 1, get label() { return i18n.t("formlar:inspectionHierarchy.uygunUygunDegil"); } },
          { value: 2, get label() { return i18n.t("formlar:inspectionHierarchy.evetHayir"); }        },
          { value: 3, get label() { return i18n.t("formlar:inspectionHierarchy.varYok"); }           },
          { value: 4, get label() { return i18n.t("formlar:inspectionHierarchy.problemVarYok"); }   },
        ],
      },
      { key: 'isPhotoRequiredPositive', dataKey: 'isPhotoRequiredPositive', get label() { return i18n.t("formlar:inspectionHierarchy.fotografZorunluPozitifCevap"); }, type: 'checkbox' },
      { key: 'isPhotoRequiredNeutral',  dataKey: 'isPhotoRequiredNeutral',  get label() { return i18n.t("formlar:inspectionHierarchy.fotografZorunluNotrNA"); }, type: 'checkbox' },
    ],
  },
  {
    key: 'hazards',  get label() { return i18n.t("formlar:inspectionHierarchy.uygunsuzluk"); },  get plural() { return i18n.t("formlar:inspectionHierarchy.uygunsuzluklar"); },
    idField: 'inspection_hazard_id',   nameField: 'inspection_hazard_name',
    color: 'warning',
    fields: [
      { key: 'name',        dataKey: 'inspection_hazard_name',        get label() { return i18n.t("formlar:inspectionHierarchy.uygunsuzlukAdi"); }, type: 'textarea', required: true },
      { key: 'description', dataKey: 'inspection_hazard_description', get label() { return i18n.t("formlar:inspectionHierarchy.aciklama"); },    type: 'text' },
    ],
  },
  {
    key: 'risks',    get label() { return i18n.t("formlar:inspectionHierarchy.tehlike"); },     get plural() { return i18n.t("formlar:inspectionHierarchy.tehlikeler"); },
    idField: 'inspection_risk_id',     nameField: 'inspection_risk_name',
    color: 'danger',
    fields: [
      { key: 'name',        dataKey: 'inspection_risk_name',        get label() { return i18n.t("formlar:inspectionHierarchy.tehlikeAdi"); }, type: 'textarea', required: true },
      { key: 'description', dataKey: 'inspection_risk_description', get label() { return i18n.t("formlar:inspectionHierarchy.aciklama"); }, type: 'text' },
    ],
  },
  {
    key: 'nonconformities', get label() { return i18n.t("formlar:inspectionHierarchy.risk"); }, get plural() { return i18n.t("formlar:inspectionHierarchy.riskler"); },
    idField: 'inspection_nonconformity_id', nameField: 'inspection_nonconformity_name',
    color: 'danger', isLeaf: true,
    fields: [
      { key: 'name',        dataKey: 'inspection_nonconformity_name',        get label() { return i18n.t("formlar:inspectionHierarchy.riskAdi"); }, type: 'textarea',   required: false },
      { key: 'description', dataKey: 'inspection_nonconformity_description', get label() { return i18n.t("formlar:inspectionHierarchy.aciklama"); },          type: 'text' },
      { key: 'olasilik', get label() { return i18n.t("formlar:inspectionHierarchy.olasilik"); }, type: 'select', options: [
          { value: '',   get label() { return i18n.t("formlar:inspectionHierarchy.seciniz"); } },
          { value: 10,   get label() { return i18n.t("formlar:inspectionHierarchy.n10BeklenirKesin"); } },
          { value: 6,    get label() { return i18n.t("formlar:inspectionHierarchy.n6YuksekOldukcaMumkun"); } },
          { value: 3,    get label() { return i18n.t("formlar:inspectionHierarchy.n3Olasi"); } },
          { value: 1,    get label() { return i18n.t("formlar:inspectionHierarchy.n1NadirenAmaMumkun"); } },
          { value: 0.5,  get label() { return i18n.t("formlar:inspectionHierarchy.n05BeklenemezAmaMumkun"); } },
          { value: 0.2,  get label() { return i18n.t("formlar:inspectionHierarchy.n02PratikteMumkunDegil"); } },
          { value: 0.1,  get label() { return i18n.t("formlar:inspectionHierarchy.n01SadeceTeorikOlarak"); } },
        ]},
      { key: 'frekans', get label() { return i18n.t("formlar:inspectionHierarchy.frekansMaruziyet"); }, type: 'select', options: [
          { value: '',   get label() { return i18n.t("formlar:inspectionHierarchy.seciniz"); } },
          { value: 10,   get label() { return i18n.t("formlar:inspectionHierarchy.n10HemenHemenSurekliSaatte"); } },
          { value: 6,    get label() { return i18n.t("formlar:inspectionHierarchy.n6SikGunde1Veya"); } },
          { value: 3,    get label() { return i18n.t("formlar:inspectionHierarchy.n3AraSiraHaftadaBir"); } },
          { value: 2,    get label() { return i18n.t("formlar:inspectionHierarchy.n2SikDegilAydaBir"); } },
          { value: 1,    get label() { return i18n.t("formlar:inspectionHierarchy.n1SeyrekYildaBirkacDefa"); } },
          { value: 0.5,  get label() { return i18n.t("formlar:inspectionHierarchy.n05CokSeyrekYilda"); } },
        ]},
      { key: 'siddet', get label() { return i18n.t("formlar:inspectionHierarchy.siddetSonuc"); }, type: 'select', options: [
          { value: '',   get label() { return i18n.t("formlar:inspectionHierarchy.seciniz"); } },
          { value: 100,  get label() { return i18n.t("formlar:inspectionHierarchy.n100BirdenFazlaOlumluKaza"); } },
          { value: 40,   get label() { return i18n.t("formlar:inspectionHierarchy.n40OlumculKazaCiddiCevresel"); } },
          { value: 15,   get label() { return i18n.t("formlar:inspectionHierarchy.n15KaliciHasarYaralanmaIs"); } },
          { value: 7,    get label() { return i18n.t("formlar:inspectionHierarchy.n7CevreselEngelOnemliHasar"); } },
          { value: 3,    get label() { return i18n.t("formlar:inspectionHierarchy.n3KucukHasarYaralanmaDahili"); } },
          { value: 1,    get label() { return i18n.t("formlar:inspectionHierarchy.n1UcuzAtlatmaCevreselZarar"); } },
        ]},
      { key: 'riskDegeri',  dataKey: 'risk_degeri',     get label() { return i18n.t("formlar:inspectionHierarchy.riskDegeri"); },       type: 'number', calculated: true },
      { key: 'oneri',       get label() { return i18n.t("formlar:inspectionHierarchy.oneriTavsiye"); },   type: 'textarea' },
    ],
  },
];

const LP_CFG = {
  key: 'legal-procedures', get label() { return i18n.t("formlar:inspectionHierarchy.yasalProsedur"); }, get plural() { return i18n.t("formlar:inspectionHierarchy.yasalProsedurler"); },
  idField: 'inspection_legal_procedure_id', nameField: 'inspection_legal_procedure_name',
  color: 'dark', isLeaf: true,
  fields: [
    { key: 'name',        dataKey: 'inspection_legal_procedure_name',        get label() { return i18n.t("formlar:inspectionHierarchy.prosedurAdi"); }, type: 'text', required: true },
    { key: 'description', dataKey: 'inspection_legal_procedure_description', get label() { return i18n.t("formlar:inspectionHierarchy.aciklama"); },     type: 'text' },
  ],
};

// Bölüm 24.1 — Suggestions konfigürasyonu (bağımsız havuz, parentId yok)
const SUGGESTION_CFG = {
  key: 'suggestions', get label() { return i18n.t("formlar:inspectionHierarchy.oneri"); }, get plural() { return i18n.t("formlar:inspectionHierarchy.oneriler"); },
  idField: 'inspection_suggestion_id', nameField: 'inspection_suggestion_name',
  color: 'warning', isLeaf: true,
  fields: [
    { key: 'name',        dataKey: 'inspection_suggestion_name',        get label() { return i18n.t("formlar:inspectionHierarchy.oneriAdi"); }, type: 'text', required: true },
    { key: 'description', dataKey: 'inspection_suggestion_description', get label() { return i18n.t("formlar:inspectionHierarchy.aciklama"); },  type: 'text' },
  ],
};

// Bölüm 24.5 — Copy Subtree: bu seviyeler kopyalanamaz (backend 400 döner)
const NON_COPYABLE = new Set(['suggestions', 'legal-procedures']);

// ── Yardımcılar ───────────────────────────────────────────────────────────────
function itemId(cfg, item)   { return item[cfg.idField]; }
function itemName(cfg, item) { return item?.translated_name || item?.[cfg.nameField] || '—'; }

// ── Yasal Prosedür Bağlayıcı (max 1, sadece edit modda) ──────────────────────
function LegalProcedureLinker({ ncId }) {
  const { t } = useTranslation("formlar");
  const [allProcs,  setAllProcs]  = useState([]);
  const [linked,    setLinked]    = useState(null); // max 1
  const [search,    setSearch]    = useState('');
  const [busy,      setBusy]      = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!ncId) return;
    Promise.all([
      adminSvc.listItems('legal-procedures', { languageId: 1 }),
      adminSvc.getLegalProceduresForNc(ncId, { languageId: 1 }),
    ]).then(([all, links]) => {
      setAllProcs(all);
      // getLegalProceduresForNc artık normalize ediyor; yine de defensive ol
      const raw = links[0] || null;
      const norm = raw && Array.isArray(raw)
        ? adminSvc.normalizeItem('legal-procedures', raw)
        : raw;
      setLinked(norm);
    }).catch(() => setLoadError(true));
  }, [ncId]);

  // Hangi field gelirse gelsin adı bul
  function getName(lp) {
    if (!lp) return '';
    return lp.translated_name
      || lp.inspection_legal_procedure_name
      || lp.name
      || '—';
  }

  // ID'yi güvenli al
  function getLpId(lp) {
    if (!lp) return null;
    return lp.inspection_legal_procedure_id ?? lp.id ?? (Array.isArray(lp) ? lp[0] : null);
  }

  const filtered = allProcs.filter((p) =>
    getName(p).toLowerCase().includes(search.toLowerCase())
  );

  async function handleLink(lp) {
    const lpId = getLpId(lp);
    if (!lpId) { alert(t("inspectionHierarchy.prosedurIdAlinamadi")); return; }
    setBusy(true);
    try {
      await adminSvc.linkLegalProcedure(ncId, lpId);
      setLinked(lp);
      setSearch('');
    } catch (e) {
      alert(e?.response?.data?.msg || t("inspectionHierarchy.baglamaBasarisiz"));
    } finally { setBusy(false); }
  }

  async function handleUnlink() {
    if (!linked) return;
    const lpId = getLpId(linked);
    if (!lpId) { alert(t("inspectionHierarchy.bagliProsedurunIdSiBulunamadi")); return; }
    setBusy(true);
    try {
      await adminSvc.unlinkLegalProcedure(ncId, lpId);
      setLinked(null);
    } catch (e) {
      alert(e?.response?.data?.msg || t("inspectionHierarchy.baglantiKesmeBasarisiz"));
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-3 pt-3 border-top">
      <div className="d-flex align-items-center gap-1 mb-2">
        <BookOpen size={14} className="text-dark" />
        <span className="fw-semibold small">{t("inspectionHierarchy.yasalProsedur")}</span>
      </div>

      {loadError ? (
        <small className="text-danger">{t("inspectionHierarchy.prosedurlerYuklenemedi")}</small>
      ) : linked ? (
        /* Bağlı prosedür var → göster + kaldır */
        <div className="d-flex align-items-center gap-2 p-2 bg-light rounded border">
          <span className="small fw-semibold flex-grow-1">{getName(linked)}</span>
          <Button color="soft-danger" size="sm" onClick={handleUnlink}
            disabled={busy} title={t("inspectionHierarchy.baglantiyiKes")}>
            {busy ? <Spinner size="sm" /> : <X size={13} />}
          </Button>
        </div>
      ) : (
        /* Bağlı prosedür yok → arama barı */
        <div>
          <Input
            type="text"
            bsSize="sm"
            placeholder={t("inspectionHierarchy.yasalProsedurAraVeBagla")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={busy}
          />
          {search.trim() ? (
            <div className="border rounded mt-1 bg-white shadow-sm"
              style={{ maxHeight: 160, overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <div className="text-muted small px-2 py-2">{t("inspectionHierarchy.sonucBulunamadi")}</div>
              ) : (
                filtered.slice(0, 10).map((lp, index) => (
                  <div
                    key={getLpId(lp) ?? `lp-${index}`}
                    className="px-2 py-2 small border-bottom"
                    style={{ cursor: busy ? 'not-allowed' : 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.classList.add('bg-light')}
                    onMouseLeave={(e) => e.currentTarget.classList.remove('bg-light')}
                    onClick={() => !busy && handleLink(lp)}
                  >
                    {getName(lp)}
                    {lp.inspection_legal_procedure_description && (
                      <div className="text-muted opacity-75" style={{ fontSize: '0.68rem' }}>
                        {lp.inspection_legal_procedure_description}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          ) : (
            <small className="text-muted">{t("inspectionHierarchy.aramakIcinBirSeylerYazin")}</small>
          )}
        </div>
      )}
    </div>
  );
}

// ── Bölüm 24.2 — SuggestionLinker: Hazard'a bağlı öneriler (çoklu, edit modda) ─
function SuggestionLinker({ hazardId }) {
  const { t } = useTranslation("formlar");
  const [allSugs,   setAllSugs]   = useState([]);
  const [linked,    setLinked]    = useState([]);
  const [search,    setSearch]    = useState('');
  const [busy,      setBusy]      = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!hazardId) return;
    Promise.all([
      adminSvc.getSuggestions({ languageId: 1 }),
      adminSvc.getSuggestionsForHazard(hazardId, { languageId: 1 }),
    ]).then(([all, links]) => {
      setAllSugs(all);
      setLinked(links);
    }).catch(() => setLoadError(true));
  }, [hazardId]);

  function getSugId(s)   { return s?.inspection_suggestion_id ?? s?.id ?? null; }
  function getSugName(s) { return s?.translated_name || s?.inspection_suggestion_name || '—'; }

  const linkedIds = new Set(linked.map(getSugId));
  const filtered  = allSugs
    .filter((s) => !linkedIds.has(getSugId(s)))
    .filter((s) => getSugName(s).toLowerCase().includes(search.toLowerCase()));

  async function handleLink(sug) {
    const sugId = getSugId(sug);
    if (!sugId) return;
    setBusy(true);
    try {
      await adminSvc.linkSuggestionToHazard(hazardId, sugId);
      setLinked((prev) => [...prev, sug]);
      setSearch('');
    } catch (e) {
      alert(e?.response?.data?.message || e?.response?.data?.msg || t("inspectionHierarchy.baglamaBasarisiz"));
    } finally { setBusy(false); }
  }

  async function handleUnlink(sug) {
    const sugId = getSugId(sug);
    if (!sugId) return;
    setBusy(true);
    try {
      await adminSvc.unlinkSuggestionFromHazard(hazardId, sugId);
      setLinked((prev) => prev.filter((s) => getSugId(s) !== sugId));
    } catch (e) {
      alert(e?.response?.data?.message || e?.response?.data?.msg || t("inspectionHierarchy.baglantiKoparmaBasarisiz"));
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-3 pt-3 border-top">
      <div className="d-flex align-items-center gap-1 mb-2">
        <Lightbulb size={14} className="text-warning" />
        <span className="fw-semibold small">{t("inspectionHierarchy.bagliOneriler")}</span>
      </div>

      {loadError ? (
        <small className="text-danger">{t("inspectionHierarchy.onerilerYuklenemedi")}</small>
      ) : (
        <>
          {linked.length > 0 ? (
            <div className="d-flex flex-wrap gap-1 mb-2">
              {linked.map((sug) => (
                <span key={getSugId(sug)}
                  className="badge d-inline-flex align-items-center gap-1 bg-warning text-dark"
                  style={{ fontSize: '0.7rem', padding: '4px 6px' }}>
                  {getSugName(sug)}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-1"
                    style={{ fontSize: '0.5rem', opacity: 0.8 }}
                    title={t("inspectionHierarchy.baglantiyiKopar")}
                    disabled={busy}
                    onClick={() => handleUnlink(sug)}
                  />
                </span>
              ))}
            </div>
          ) : (
            <small className="text-muted d-block mb-2">{t("inspectionHierarchy.henuzOneriBaglanmamis")}</small>
          )}
          <Input type="text" bsSize="sm"
            placeholder={t("inspectionHierarchy.oneriAraVeBagla")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={busy}
          />
          {search.trim() && (
            <div className="border rounded mt-1 bg-white shadow-sm"
              style={{ maxHeight: 150, overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <div className="text-muted small px-2 py-2">{t("inspectionHierarchy.sonucBulunamadi")}</div>
              ) : (
                filtered.slice(0, 10).map((sug, i) => (
                  <div
                    key={getSugId(sug) ?? `sug-${i}`}
                    className="px-2 py-2 small border-bottom"
                    style={{ cursor: busy ? 'not-allowed' : 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.classList.add('bg-light')}
                    onMouseLeave={(e) => e.currentTarget.classList.remove('bg-light')}
                    onClick={() => !busy && handleLink(sug)}
                  >
                    {getSugName(sug)}
                    {sug.inspection_suggestion_description && (
                      <div className="text-muted opacity-75" style={{ fontSize: '0.68rem' }}>
                        {sug.inspection_suggestion_description}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Bölüm 24.3 — HazardLPLinker: Hazard seviyesinde yasal prosedür bağlayıcı ──
function HazardLPLinker({ hazardId }) {
  const { t } = useTranslation("formlar");
  const [allProcs,  setAllProcs]  = useState([]);
  const [linked,    setLinked]    = useState([]);
  const [search,    setSearch]    = useState('');
  const [busy,      setBusy]      = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!hazardId) return;
    Promise.all([
      adminSvc.listItems('legal-procedures', { languageId: 1 }),
      adminSvc.getLegalProceduresForHazard(hazardId, { languageId: 1 }),
    ]).then(([all, links]) => {
      setAllProcs(all);
      setLinked(links);
    }).catch(() => setLoadError(true));
  }, [hazardId]);

  function getLpId(lp)   { return lp?.inspection_legal_procedure_id ?? lp?.id ?? null; }
  function getLpName(lp) { return lp?.translated_name || lp?.inspection_legal_procedure_name || '—'; }

  const linkedIds = new Set(linked.map(getLpId));
  const filtered  = allProcs
    .filter((lp) => !linkedIds.has(getLpId(lp)))
    .filter((lp) => getLpName(lp).toLowerCase().includes(search.toLowerCase()));

  async function handleLink(lp) {
    const lpId = getLpId(lp);
    if (!lpId) return;
    setBusy(true);
    try {
      await adminSvc.linkLegalProcedureToHazard(hazardId, lpId);
      setLinked((prev) => [...prev, lp]);
      setSearch('');
    } catch (e) {
      alert(e?.response?.data?.message || e?.response?.data?.msg || t("inspectionHierarchy.baglamaBasarisiz"));
    } finally { setBusy(false); }
  }

  async function handleUnlink(lp) {
    const lpId = getLpId(lp);
    if (!lpId) return;
    setBusy(true);
    try {
      await adminSvc.unlinkLegalProcedureFromHazard(hazardId, lpId);
      setLinked((prev) => prev.filter((l) => getLpId(l) !== lpId));
    } catch (e) {
      alert(e?.response?.data?.message || e?.response?.data?.msg || t("inspectionHierarchy.baglantiKoparmaBasarisiz"));
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-3 pt-3 border-top">
      <div className="d-flex align-items-center gap-1 mb-2">
        <BookOpen size={14} className="text-dark" />
        <span className="fw-semibold small">
          {t("inspectionHierarchy.bagliMevzuatlar")}
          <span className="text-muted fw-normal ms-1" style={{ fontSize: '0.7rem' }}>{t("inspectionHierarchy.hazardSeviyesiYeni")}</span>
        </span>
      </div>

      {loadError ? (
        <small className="text-danger">{t("inspectionHierarchy.mevzuatlarYuklenemedi")}</small>
      ) : (
        <>
          {linked.length > 0 ? (
            <div className="d-flex flex-wrap gap-1 mb-2">
              {linked.map((lp) => (
                <span key={getLpId(lp)}
                  className="badge d-inline-flex align-items-center gap-1 bg-dark text-white"
                  style={{ fontSize: '0.7rem', padding: '4px 6px' }}>
                  {getLpName(lp)}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-1"
                    style={{ fontSize: '0.5rem', opacity: 0.8 }}
                    title={t("inspectionHierarchy.baglantiyiKopar")}
                    disabled={busy}
                    onClick={() => handleUnlink(lp)}
                  />
                </span>
              ))}
            </div>
          ) : (
            <small className="text-muted d-block mb-2">{t("inspectionHierarchy.henuzMevzuatBaglanmamis")}</small>
          )}
          <Input type="text" bsSize="sm"
            placeholder={t("inspectionHierarchy.mevzuatAraVeBagla")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={busy}
          />
          {search.trim() && (
            <div className="border rounded mt-1 bg-white shadow-sm"
              style={{ maxHeight: 150, overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <div className="text-muted small px-2 py-2">{t("inspectionHierarchy.sonucBulunamadi")}</div>
              ) : (
                filtered.slice(0, 10).map((lp, i) => (
                  <div
                    key={getLpId(lp) ?? `lp-h-${i}`}
                    className="px-2 py-2 small border-bottom"
                    style={{ cursor: busy ? 'not-allowed' : 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.classList.add('bg-light')}
                    onMouseLeave={(e) => e.currentTarget.classList.remove('bg-light')}
                    onClick={() => !busy && handleLink(lp)}
                  >
                    {getLpName(lp)}
                    {lp.inspection_legal_procedure_description && (
                      <div className="text-muted opacity-75" style={{ fontSize: '0.68rem' }}>
                        {lp.inspection_legal_procedure_description}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── NC Suggester Panel — hazard altındaki önceki riskleri listeler (modal içi) ─
function NcSuggesterPanel({ hazardId, onSelect }) {
  const { t } = useTranslation("formlar");
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [open,    setOpen]    = useState(true);

  useEffect(() => {
    if (!hazardId) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await adminSvc.getHazardNonconformities(hazardId, { languageId: 1 });
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [hazardId]);

  if (!hazardId) return null;

  function badgeColor(val) {
    if (!val) return 'secondary';
    if (val < 20)  return 'success';
    if (val < 70)  return 'warning';
    if (val < 200) return 'danger';
    return 'dark';
  }

  return (
    <div className="mb-3 border rounded overflow-hidden" style={{ borderColor: '#fde68a' }}>
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2"
        style={{ background: '#fef3c7', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(p => !p)}
      >
        <span className="d-flex align-items-center gap-2 fw-semibold small" style={{ color: '#92400e' }}>
          <Lightbulb size={13} />{" "}{t("inspectionHierarchy.oncekiRisklerdenDoldur")}
        </span>
        <ChevronDown size={13} style={{ color: '#92400e', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
      </div>
      {open && (
        <div style={{ maxHeight: 180, overflowY: 'auto', background: '#fffbeb', padding: '4px 8px' }}>
          {loading ? (
            <div className="text-center py-2"><Spinner size="sm" color="warning" /></div>
          ) : items.length === 0 ? (
            <p className="text-muted text-center small py-2 mb-0">{t("inspectionHierarchy.buUygunsuzlukIcinHenuzKayitli")}</p>
          ) : (
            items.map((s) => (
              <div
                key={s.inspection_nonconformity_id}
                className="d-flex align-items-center justify-content-between gap-2 p-2 rounded mb-1"
                style={{ background: '#fff', border: '1px solid #fde68a', cursor: 'pointer' }}
                title={t("inspectionHierarchy.tiklaFormaDoldur")}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#fef9c3'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                onClick={() => onSelect(s)}
              >
                <div className="flex-grow-1 overflow-hidden">
                  <div className="fw-semibold text-truncate" style={{ fontSize: '0.75rem' }}>
                    {s.nonconformity_name || '—'}
                  </div>
                  {s.risk_name && (
                    <div className="text-muted text-truncate" style={{ fontSize: '0.67rem' }}>
                      ↳ {s.risk_name}
                    </div>
                  )}
                </div>
                {s.risk_degeri != null && (
                  <Badge color={badgeColor(s.risk_degeri)} pill style={{ fontSize: '0.65rem', flexShrink: 0 }}>
                    {s.risk_degeri}
                  </Badge>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Ekle / Düzenle Modalı ─────────────────────────────────────────────────────
function ItemModal({ open, levelCfg, parentId, item, onSave, onClose, optionsMap = {}, hazardId }) {
  const { t } = useTranslation("formlar");
  const isEdit = !!item;
  const isNc   = levelCfg?.key === 'nonconformities';

  const [form,      setForm]      = useState({});
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    if (!open) return;
    async function run() {
      const init = {};
      levelCfg.fields.forEach((f) => {
        init[f.key] = item?.[f.dataKey ?? f.key] ?? (f.type === 'checkbox' ? false : f.type === 'number' ? '' : '');
      });
      setForm(init);
      setError(null);
    }
    run();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Suggester'dan öneri seçildiğinde formu doldur
  function applysuggestion(s) {
    setForm((prev) => {
      const o  = Number(s.olasilik ?? prev.olasilik ?? 0);
      const f  = Number(s.frekans  ?? prev.frekans  ?? 0);
      const sv = Number(s.siddet   ?? prev.siddet   ?? 0);
      return {
        ...prev,
        name:       s.nonconformity_name ?? prev.name,
        olasilik:   s.olasilik  != null ? s.olasilik  : prev.olasilik,
        frekans:    s.frekans   != null ? s.frekans   : prev.frekans,
        siddet:     s.siddet    != null ? s.siddet    : prev.siddet,
        riskDegeri: (o > 0 && f > 0 && sv > 0) ? +(o * f * sv).toFixed(2) : prev.riskDegeri,
        oneri:      s.oneri     != null ? s.oneri     : prev.oneri,
      };
    });
  }

  function set(key, value) {
    setForm((p) => {
      const updated = { ...p, [key]: value };
      if (['olasilik', 'frekans', 'siddet'].includes(key)) {
        const o = Number(key === 'olasilik' ? value : updated.olasilik);
        const f = Number(key === 'frekans'  ? value : updated.frekans);
        const s = Number(key === 'siddet'   ? value : updated.siddet);
        updated.riskDegeri = (o > 0 && f > 0 && s > 0) ? +(o * f * s).toFixed(2) : '';
      }
      return updated;
    });
  }

  async function submit() {
    const missing = levelCfg.fields.find((f) => f.required && !form[f.key]);
    if (missing) { setError(t("inspectionHierarchy.alaniZorunludur", { label: missing.label })); return; }
    setSaving(true); setError(null);
    try {
      const body = { ...form };
      if (!isEdit && parentId != null) body.parentId = parentId;
      levelCfg.fields.forEach((f) => {
        if ((f.type === 'number' || ['olasilik','frekans','siddet','riskDegeri'].includes(f.key)) && body[f.key] !== '') body[f.key] = Number(body[f.key]);
        if (f.type === 'checkbox')                       body[f.key] = body[f.key] ? 1 : 0;
        if (f.numeric) {
          if (body[f.key] !== '' && body[f.key] != null) body[f.key] = Number(body[f.key]);
          else delete body[f.key];
        }
      });
      // Boş opsiyonel alanları kaldır — backend "" değerini reddediyor (400)
      Object.keys(body).forEach((key) => {
        if (body[key] === '') {
          const fieldCfg = levelCfg.fields.find((f) => f.key === key);
          if (!fieldCfg || !fieldCfg.required) delete body[key];
        }
      });
      if (isEdit) {
        await adminSvc.updateItem(levelCfg.key, itemId(levelCfg, item), body);
      } else {
      await adminSvc.createItem(levelCfg.key, body);
      }
      onSave();
    } catch (e) {
      setError(e?.response?.data?.msg || t("inspectionHierarchy.islemBasarisizOldu"));
    } finally { setSaving(false); }
  }

  if (!levelCfg) return null;

  return (
    <Modal isOpen={open} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>
        {isEdit ? t("inspectionHierarchy.duzenle", { label: levelCfg.label }) : t("inspectionHierarchy.yeniEkle", { label: levelCfg.label })}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger" className="py-2 small">{error}</Alert>}
        {/* Risk Suggester — sadece nonconformity (Risk) eklerken/düzenlerken göster */}
        {isNc && hazardId && (
          <NcSuggesterPanel hazardId={hazardId} onSelect={applysuggestion} />
        )}
        {levelCfg.fields.filter(f => !f.hidden).map((f) => (
          <FormGroup key={f.key} className={f.type === 'checkbox' ? 'mb-2' : 'mb-3'}>
            {f.type !== 'checkbox' && (
              <Label className="fw-semibold small mb-1">
                {f.label}
                {f.required && <span className="text-danger ms-1">*</span>}
              </Label>
            )}
            {f.type === 'select' ? (
              <Input type="select" bsSize="sm" value={form[f.key] ?? ''}
                onChange={(e) => set(f.key, e.target.value)}>
                {(optionsMap[f.key] || f.options || []).map((o) =>
                  <option key={o.value} value={o.value}>{o.label}</option>
                )}
              </Input>
            ) : f.type === 'textarea' ? (
              <Input type="textarea" rows={3} bsSize="sm" value={form[f.key] ?? ''}
                onChange={(e) => set(f.key, e.target.value)} />
            ) : f.type === 'checkbox' ? (
              <div className="form-check">
                <input type="checkbox" className="form-check-input" id={`chk-${f.key}`}
                  checked={!!form[f.key]} onChange={(e) => set(f.key, e.target.checked)} />
                <label className="form-check-label small" htmlFor={`chk-${f.key}`}>{f.label}</label>
              </div>
            ) : (
              <Input type={f.type} bsSize="sm" value={form[f.key] ?? ''}
                maxLength={f.maxLength} min={f.min} max={f.max}
                readOnly={!!f.calculated}
                className={f.calculated ? 'bg-light' : ''}
                onChange={(e) => !f.calculated && set(f.key, e.target.value)} />
            )}
            {f.calculated && (
              <small className="text-muted">{t("inspectionHierarchy.olasilikFrekansSiddet")}</small>
            )}
            {/* 10.5: NC name opsiyonel → hint text */}
            {levelCfg?.key === 'nonconformities' && f.key === 'name' && (
              <small className="text-muted d-block mt-1">
                {t("inspectionHierarchy.bosBirakirsanizUstSorununAdindan")}
              </small>
            )}
          </FormGroup>
        ))}
        {/* Bölüm 24.2 & 24.3 — Hazard junction panelleri (edit modda) */}
        {isEdit && levelCfg.key === 'hazards' && (
          <>
            <SuggestionLinker hazardId={item?.inspection_hazard_id} />
            <HazardLPLinker   hazardId={item?.inspection_hazard_id} />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" size="sm" onClick={onClose}>{t("common:iptal")}</Button>
        <Button color="primary" size="sm" onClick={submit} disabled={saving}>
          {saving ? <Spinner size="sm" /> : isEdit ? t("inspectionHierarchy.guncelle") : t("common:ekle")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ── Toplu Fotoğraf Modu Butonu (Kategoriler için) ────────────────────────────
const PHOTO_MODE_OPTIONS = [
  { value: 'positiveOnly', get label() { return i18n.t("formlar:inspectionHierarchy.pozitifCevap"); },   get desc() { return i18n.t("formlar:inspectionHierarchy.sadeceUygunCevabindaFotografZorunlu"); } },
  { value: 'neutralOnly',  get label() { return i18n.t("formlar:inspectionHierarchy.notrNA"); },       get desc() { return i18n.t("formlar:inspectionHierarchy.sadeceNACevabindaFotograf"); }  },
  { value: 'both',         get label() { return i18n.t("formlar:inspectionHierarchy.herIkisi"); },         get desc() { return i18n.t("formlar:inspectionHierarchy.pozitifVeNotrCevaplardaFotograf"); } },
  { value: 'none',         get label() { return i18n.t("formlar:inspectionHierarchy.hicbiri"); },           get desc() { return i18n.t("formlar:inspectionHierarchy.fotografZorunluluguKaldirilir"); }           },
];

function BulkPhotoModeButton({ categoryId, categoryName }) {
  const { t } = useTranslation("formlar");
  const [open,  setOpen]  = useState(false);
  const [busy,  setBusy]  = useState(false);

  async function handleSelect(mode) {
    const opt = PHOTO_MODE_OPTIONS.find(o => o.value === mode);
    const confirmed = window.confirm(
      t("inspectionHierarchy.kategorisindekiTumSorularinFotografModu", { categoryName, label: opt?.label })
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      await adminSvc.bulkSetPhotoMode(categoryId, mode);
      alert(t("inspectionHierarchy.tumSorularGuncellendi"));
    } catch (e) {
      alert(e?.response?.data?.msg || t("inspectionHierarchy.guncellemeBasarisiz"));
    } finally { setBusy(false); }
  }

  return (
    <Dropdown isOpen={open} toggle={() => setOpen(v => !v)} direction="down">
      <DropdownToggle color="soft-secondary" size="sm" disabled={busy}
        className="d-flex align-items-center gap-1" title={t("inspectionHierarchy.kategoridekiTumSorularinFotografModunu")}>
        {busy ? <Spinner size="sm" /> : <Camera size={12} />}
        <ChevronDown size={10} />
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem header>{t("inspectionHierarchy.fotografModuToplu")}</DropdownItem>
        {PHOTO_MODE_OPTIONS.map(opt => (
          <DropdownItem key={opt.value} onClick={() => handleSelect(opt.value)}>
            <div>{opt.label}</div>
            <small className="text-muted">{opt.desc}</small>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}

// ── Soft-Delete Onay Modalı ───────────────────────────────────────────────────
function DeleteModal({ open, levelCfg, item, onConfirm, onClose }) {
  const { t } = useTranslation("formlar");
  const [busy, setBusy] = useState(false);
  async function confirm() {
    if (!levelCfg || !item) return;
    setBusy(true);
    try {
      await adminSvc.softDeleteItem(levelCfg.key, itemId(levelCfg, item));
      onConfirm();
    } catch (e) { alert(e?.response?.data?.msg || t("inspectionHierarchy.silmeBasarisiz")); }
    finally { setBusy(false); }
  }
  return (
    <Modal isOpen={open} toggle={onClose} size="sm" centered>
      <ModalHeader toggle={onClose}>{t("inspectionHierarchy.ogeyiSil")}</ModalHeader>
      <ModalBody>
        <p className="mb-1">
          <strong>{item && levelCfg ? itemName(levelCfg, item) : ''}</strong>{" "}{t("inspectionHierarchy.silinecek")}
        </p>
        <small className="text-muted">{t("inspectionHierarchy.softDeleteVeriKorunurListeden")}</small>
      </ModalBody>
      <ModalFooter>
        <Button color="light" size="sm" onClick={onClose}>{t("common:iptal")}</Button>
        <Button color="danger" size="sm" onClick={confirm} disabled={busy}>
          {busy ? <Spinner size="sm" /> : <><Trash2 size={13} className="me-1" />{t("common:sil")}</>}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ── Bölüm 2.1 — Hiyerarşik Seçici ───────────────────────────────────────────
// targetLevelKey: hangi seviyedeki öğe seçilecek
// onPick: (id, name) => void
function HierarchicalPicker({ targetLevelKey, onPick }) {
  const { t } = useTranslation("formlar");
  const targetIdx      = LEVEL_CONFIG.findIndex((c) => c.key === targetLevelKey);
  const [pickerPath,   setPickerPath]   = useState([]);
  const [pickerItems,  setPickerItems]  = useState([]);
  const [loading,      setLoading]      = useState(false);

  const pickerLevelIdx = pickerPath.length;
  const pickerLevelCfg = LEVEL_CONFIG[pickerLevelIdx];
  const pickerParentId = pickerPath.length > 0 ? pickerPath[pickerPath.length - 1].id : undefined;

  useEffect(() => {
    if (!pickerLevelCfg || pickerLevelIdx > targetIdx) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await adminSvc.listItems(pickerLevelCfg.key, {
          parentId: pickerParentId,
          languageId: 1,
        });
        if (!cancelled) setPickerItems(data);
      } catch {
        if (!cancelled) setPickerItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [pickerLevelCfg, pickerParentId, pickerLevelIdx, targetIdx]);

  if (pickerLevelIdx > targetIdx) return null;

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap gap-1 mb-2" style={{ fontSize: '0.72rem' }}>
        <button className="btn py-0 px-1 btn-light border" style={{ fontSize: '0.7rem' }}
          onClick={() => setPickerPath([])}>{t("inspectionHierarchy.birimler")}</button>
        {pickerPath.map((seg, i) => (
          <React.Fragment key={i}>
            <ChevronRight size={9} className="text-muted" />
            <button className="btn py-0 px-1 btn-light border" style={{ fontSize: '0.7rem' }}
              onClick={() => setPickerPath((p) => p.slice(0, i + 1))}>{seg.name}</button>
          </React.Fragment>
        ))}
      </div>
      <small className="text-muted d-block mb-1">
        <strong>{pickerLevelCfg?.label}</strong>{" "}{t("inspectionHierarchy.secin2")}
        {pickerLevelIdx < targetIdx && <span className="ms-1 text-primary">{t("inspectionHierarchy.devamEdecek")}</span>}
        {pickerLevelIdx === targetIdx && <span className="ms-1 text-success fw-semibold">{t("inspectionHierarchy.hedefSeviye")}</span>}
      </small>
      {loading ? (
        <div className="text-center py-2"><Spinner size="sm" /></div>
      ) : (
        <div style={{ maxHeight: 180, overflowY: 'auto' }} className="border rounded bg-white">
          {pickerItems.length === 0 ? (
            <div className="text-muted small px-2 py-2">{t("inspectionHierarchy.buSeviyedeOgeBulunamadi")}</div>
          ) : pickerItems.map((it) => {
            const id      = itemId(pickerLevelCfg, it);
            const name    = itemName(pickerLevelCfg, it);
            const isTarget = pickerLevelIdx === targetIdx;
            return (
              <div key={id}
                className="px-2 py-2 small border-bottom d-flex align-items-center justify-content-between"
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => e.currentTarget.classList.add('bg-light')}
                onMouseLeave={(e) => e.currentTarget.classList.remove('bg-light')}
                onClick={() => {
                  if (isTarget) {
                    onPick(id, name);
                  } else {
                    setPickerPath((prev) => [...prev, { levelIdx: pickerLevelIdx, id, name }]);
                  }
                }}
              >
                <span className={isTarget ? 'fw-semibold text-success' : ''}>{name}</span>
                <span className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: '0.67rem' }}>
                  #{id}
                  {!isTarget && <ChevronRight size={11} className="ms-1" />}
                  {isTarget  && <span className="text-success ms-1">{t("inspectionHierarchy.sec")}</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Bölüm 2.1 — Flat Liste Seçici ────────────────────────────────────────────
function FlatListPicker({ targetLevelKey, onPick }) {
  const { t } = useTranslation("formlar");
  const targetLvl      = LEVEL_CONFIG.find((c) => c.key === targetLevelKey);
  const [items,   setItems]   = useState([]);
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!targetLevelKey) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await adminSvc.listItems(targetLevelKey, { languageId: 1 });
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [targetLevelKey]);

  const filtered = items.filter((it) =>
    itemName(targetLvl, it).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <Input type="text" bsSize="sm"
        placeholder={t("inspectionHierarchy.ara", { v: targetLvl?.label || t("inspectionHierarchy.oge") })}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-1"
      />
      {loading ? (
        <div className="text-center py-2"><Spinner size="sm" /></div>
      ) : (
        <div style={{ maxHeight: 200, overflowY: 'auto' }} className="border rounded bg-white">
          {filtered.length === 0 ? (
            <div className="text-muted small px-2 py-2">{t("inspectionHierarchy.sonucBulunamadi")}</div>
          ) : filtered.slice(0, 30).map((it) => {
            const id   = itemId(targetLvl, it);
            const name = itemName(targetLvl, it);
            return (
              <div key={id}
                className="px-2 py-2 small border-bottom d-flex align-items-center justify-content-between"
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => e.currentTarget.classList.add('bg-light')}
                onMouseLeave={(e) => e.currentTarget.classList.remove('bg-light')}
                onClick={() => onPick(id, name)}
              >
                <span className="fw-semibold text-success">{name}</span>
                <span className="text-muted" style={{ fontSize: '0.67rem' }}>{t("inspectionHierarchy.sec2", { id })}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Bölüm 24.5 / 2.1 — Copy Subtree Modal (hedef seçimi: 3 mod) ──────────────
// 3 mod: Hiyerarşik | Flat Liste | ID ile
function CopyModal({ open, levelCfg, item, onClose, onSuccess }) {
  const { t } = useTranslation("formlar");
  const [mode,           setMode]           = useState('hierarchical');
  const [selectedTarget, setSelectedTarget] = useState(null); // { id, name }
  const [manualId,       setManualId]       = useState('');
  const [busy,           setBusy]           = useState(false);
  const [error,          setError]          = useState(null);

  const isRoot    = levelCfg?.key === 'departments';
  const parentLvl = LEVEL_CONFIG[LEVEL_CONFIG.findIndex((c) => c.key === levelCfg?.key) - 1];

  const resolvedTargetId = mode === 'manual'
    ? (manualId ? Number(manualId) : null)
    : (selectedTarget?.id ?? null);

  function resetPicker() { setSelectedTarget(null); setManualId(''); setError(null); }

  async function handleCopy() {
    if (!levelCfg || !item) return;
    const targetId = isRoot ? null : resolvedTargetId;
    if (!isRoot && !targetId) { setError(t("inspectionHierarchy.hedefSecilmesiZorunludur")); return; }
    setBusy(true); setError(null);
    try {
      const res = await adminSvc.copySubtree(levelCfg.key, itemId(levelCfg, item), targetId);
      if (res?.result === false) { setError(res.message || t("inspectionHierarchy.kopyalamaBasarisiz")); return; }
      onSuccess(res?.data?.copiedCount ?? 0, res?.data?.newRootId);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.msg || t("inspectionHierarchy.kopyalamaBasarisiz"));
    } finally { setBusy(false); }
  }

  if (!levelCfg || !item) return null;

  return (
    <Modal isOpen={open} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>{t("inspectionHierarchy.altAgaciKopyala")}</ModalHeader>
      <ModalBody>
        <Alert color="warning" className="py-2 small mb-3">
          {t("inspectionHierarchy.buIslem")}{" "}<strong>{t("inspectionHierarchy.geriAlinamaz")}</strong>.
        </Alert>
        <p className="small mb-1">
          {t("inspectionHierarchy.kopyalanacak")}{" "}<strong>{levelCfg.label} — {item ? itemName(levelCfg, item) : ''}</strong>
        </p>
        <p className="small text-muted mb-3">
          {t("inspectionHierarchy.buKayitVeTumAlt")}
        </p>

        {!isRoot && (
          <>
            <Label className="fw-semibold small">
              {t("inspectionHierarchy.hedefSec", { label: parentLvl?.label })}{" "}<span className="text-danger">*</span>
            </Label>
            {/* Mod sekmeleri */}
            <div className="d-flex gap-2 mt-1 mb-3">
              {[
                { key: 'hierarchical', label: t("inspectionHierarchy.hiyerarsik") },
                { key: 'flat',         label: t("inspectionHierarchy.liste")       },
                { key: 'manual',       label: t("inspectionHierarchy.idIle")      },
              ].map((m) => (
                <button key={m.key}
                  className={`btn btn-sm ${mode === m.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => { setMode(m.key); resetPicker(); }}>
                  {m.label}
                </button>
              ))}
            </div>

            {/* Seçilmiş hedef göstergesi */}
            {selectedTarget && (
              <Alert color="success" className="py-2 small mb-2 d-flex align-items-center gap-2">
                {t("inspectionHierarchy.secilenHedef")}{" "}<strong>{selectedTarget.name}</strong>
                <span className="text-muted">(#{selectedTarget.id})</span>
                <button className="btn btn-sm ms-auto py-0 px-1" onClick={() => setSelectedTarget(null)}>{t("inspectionHierarchy.degistir")}</button>
              </Alert>
            )}

            {!selectedTarget && mode === 'hierarchical' && parentLvl && (
              <HierarchicalPicker key={`copy-hier-${open}`}
                targetLevelKey={parentLvl.key}
                onPick={(id, name) => setSelectedTarget({ id, name })} />
            )}
            {!selectedTarget && mode === 'flat' && parentLvl && (
              <FlatListPicker key={`copy-flat-${open}`}
                targetLevelKey={parentLvl.key}
                onPick={(id, name) => setSelectedTarget({ id, name })} />
            )}
            {mode === 'manual' && (
              <FormGroup className="mb-0">
                <Input type="number" bsSize="sm"
                  placeholder={t("inspectionHierarchy.idGirin", { label: parentLvl?.label })}
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                  disabled={busy}
                />
                {parentLvl && (
                  <small className="text-muted">
                    {t("inspectionHierarchy.dan", { label: levelCfg.label })}{" "}<strong>{t("inspectionHierarchy.birSeviyeUst")}</strong>{t("inspectionHierarchy.tekiIdSiGirin", { label: parentLvl.label })}
                  </small>
                )}
              </FormGroup>
            )}
          </>
        )}

        {error && <Alert color="danger" className="py-2 small mt-2">{error}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" size="sm" onClick={onClose}>{t("common:iptal")}</Button>
        <Button color="warning" size="sm" onClick={handleCopy} disabled={busy}>
          {busy ? <Spinner size="sm" /> : <><Copy size={13} className="me-1" />{t("inspectionHierarchy.kopyala")}</>}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ── Bölüm 2.2 — Pull Modal (Başka Yerden Çek) ────────────────────────────────
// Hedefte duruyorsun → kaynak seç → kopyala burada oluştur.
// targetParentId = mevcut görüntülenen parent'ın ID'si
function PullModal({ open, levelCfg, targetParentId, onClose, onSuccess }) {
  const { t } = useTranslation("formlar");
  const [mode,           setMode]           = useState('hierarchical');
  const [selectedSource, setSelectedSource] = useState(null); // { id, name }
  const [manualId,       setManualId]       = useState('');
  const [busy,           setBusy]           = useState(false);
  const [error,          setError]          = useState(null);

  const resolvedSourceId = mode === 'manual'
    ? (manualId ? Number(manualId) : null)
    : (selectedSource?.id ?? null);

  function resetPicker() { setSelectedSource(null); setManualId(''); setError(null); }

  async function handlePull() {
    if (!levelCfg) return;
    const sourceId = resolvedSourceId;
    if (!sourceId) { setError(t("inspectionHierarchy.kaynakOgeSecilmesiZorunludur")); return; }
    setBusy(true); setError(null);
    try {
      const res = await adminSvc.copySubtree(levelCfg.key, sourceId, targetParentId ?? null);
      if (res?.result === false) { setError(res.message || t("inspectionHierarchy.kopyalamaBasarisiz")); return; }
      onSuccess(res?.data?.copiedCount ?? 0, res?.data?.newRootId);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.msg || t("inspectionHierarchy.kopyalamaBasarisiz"));
    } finally { setBusy(false); }
  }

  if (!levelCfg) return null;

  return (
    <Modal isOpen={open} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>
        {t("inspectionHierarchy.baskaYerdenCek", { label: levelCfg.label })}
      </ModalHeader>
      <ModalBody>
        <Alert color="warning" className="py-2 small mb-3">
          {t("inspectionHierarchy.buIslem")}{" "}<strong>{t("inspectionHierarchy.geriAlinamaz")}</strong>{t("inspectionHierarchy.secilenKayitVeTumAlt")}
        </Alert>
        <p className="small mb-3 text-muted">
          {t("inspectionHierarchy.kopyalamakIstediginiz")}{" "}<strong>{levelCfg.label}</strong>{t("inspectionHierarchy.iSecinSecilenKayitVe")}
        </p>

        {/* Mod sekmeleri */}
        <div className="d-flex gap-2 mb-3">
          {[
            { key: 'hierarchical', label: t("inspectionHierarchy.hiyerarsik") },
            { key: 'flat',         label: t("inspectionHierarchy.liste")       },
            { key: 'manual',       label: t("inspectionHierarchy.idIle")      },
          ].map((m) => (
            <button key={m.key}
              className={`btn btn-sm ${mode === m.key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => { setMode(m.key); resetPicker(); }}>
              {m.label}
            </button>
          ))}
        </div>

        {/* Seçilmiş kaynak göstergesi */}
        {selectedSource && (
          <Alert color="success" className="py-2 small mb-2 d-flex align-items-center gap-2">
            {t("inspectionHierarchy.kaynak")}{" "}<strong>{selectedSource.name}</strong>
            <span className="text-muted">(#{selectedSource.id})</span>
            <button className="btn btn-sm ms-auto py-0 px-1" onClick={() => setSelectedSource(null)}>{t("inspectionHierarchy.degistir")}</button>
          </Alert>
        )}

        {!selectedSource && mode === 'hierarchical' && (
          <HierarchicalPicker key={`pull-hier-${open}`}
            targetLevelKey={levelCfg.key}
            onPick={(id, name) => setSelectedSource({ id, name })} />
        )}
        {!selectedSource && mode === 'flat' && (
          <FlatListPicker key={`pull-flat-${open}`}
            targetLevelKey={levelCfg.key}
            onPick={(id, name) => setSelectedSource({ id, name })} />
        )}
        {mode === 'manual' && (
          <FormGroup className="mb-0">
            <Label className="fw-semibold small mb-1">
              {t("inspectionHierarchy.kaynakId", { label: levelCfg.label })}{" "}<span className="text-danger">*</span>
            </Label>
            <Input type="number" bsSize="sm"
              placeholder={t("inspectionHierarchy.kopyalanacakIdSi", { label: levelCfg.label })}
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              disabled={busy}
            />
          </FormGroup>
        )}

        {error && <Alert color="danger" className="py-2 small mt-2">{error}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" size="sm" onClick={onClose}>{t("common:iptal")}</Button>
        <Button color="info" size="sm" onClick={handlePull} disabled={busy}>
          {busy ? <Spinner size="sm" /> : <><Copy size={13} className="me-1" />{t("inspectionHierarchy.burayaKopyala")}</>}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ── Yasal Prosedürler Kartı ───────────────────────────────────────────────────
function LegalProceduresCard() {
  const { t } = useTranslation("formlar");
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [modal,    setModal]    = useState({ open: false, item: null });
  const [delModal, setDelModal] = useState({ open: false, item: null });

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await adminSvc.listItems('legal-procedures', { languageId: 1 })); }
    catch { /* sessiz */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { async function run() { await load(); } run(); }, [load]);

  return (
    <Card className="border-0 shadow-sm mt-4">
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-2">
            <BookOpen size={18} className="text-dark" />
            <h6 className="fw-semibold mb-0">{t("inspectionHierarchy.yasalProsedurler")}</h6>
            <Badge color="dark" pill>{items.length}</Badge>
          </div>
          <Button color="primary" size="sm" className="d-flex align-items-center gap-1"
            onClick={() => setModal({ open: true, item: null })}>
            <Plus size={13} />{" "}{t("inspectionHierarchy.yeniProsedur")}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-3"><Spinner size="sm" /></div>
        ) : items.length === 0 ? (
          <p className="text-muted small text-center py-3">{t("inspectionHierarchy.henuzYasalProsedurEklenmemis")}</p>
        ) : (
          <div className="table-responsive">
            <Table hover bordered={false} className="align-middle mb-0 small">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>{t("inspectionHierarchy.ad")}</th>
                  <th>{t("inspectionHierarchy.aciklama")}</th>
                  <th style={{ width: 90 }} className="text-center">{t("inspectionHierarchy.islemler")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const id   = item.inspection_legal_procedure_id;
                  const name = item.translated_name || item.inspection_legal_procedure_name;
                  return (
                    <tr key={id}>
                      <td className="text-muted">{id}</td>
                      <td className="fw-semibold">{name}</td>
                      <td className="text-muted">{item.inspection_legal_procedure_description || '—'}</td>
                      <td>
                        <div className="d-flex justify-content-center gap-1">
                          <Button color="soft-primary" size="sm"
                            onClick={() => setModal({ open: true, item })}>
                            <Edit2 size={13} />
                          </Button>
                          <Button color="soft-danger" size="sm"
                            onClick={() => setDelModal({ open: true, item })}>
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>

      <ItemModal
        key={`lp-item-${modal.open}`}
        open={modal.open} levelCfg={LP_CFG} parentId={null}
        item={modal.item}
        onSave={() => { setModal({ open: false, item: null }); load(); }}
        onClose={() => setModal({ open: false, item: null })}
      />
      <DeleteModal
        open={delModal.open} levelCfg={LP_CFG} item={delModal.item}
        onConfirm={() => { setDelModal({ open: false, item: null }); load(); }}
        onClose={() => setDelModal({ open: false, item: null })}
      />
    </Card>
  );
}

// ── Bölüm 24.1 — Öneriler Yönetim Kartı ──────────────────────────────────────
function SuggestionsCard() {
  const { t } = useTranslation("formlar");
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [modal,    setModal]    = useState({ open: false, item: null });
  const [delModal, setDelModal] = useState({ open: false, item: null });

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await adminSvc.getSuggestions({ languageId: 1 })); }
    catch { /* sessiz */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { async function run() { await load(); } run(); }, [load]);

  return (
    <Card className="border-0 shadow-sm mt-4">
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-2">
            <Lightbulb size={18} className="text-warning" />
            <h6 className="fw-semibold mb-0">{t("inspectionHierarchy.oneriler")}</h6>
            <Badge color="warning" pill>{items.length}</Badge>
          </div>
          <Button color="primary" size="sm" className="d-flex align-items-center gap-1"
            onClick={() => setModal({ open: true, item: null })}>
            <Plus size={13} />{" "}{t("inspectionHierarchy.yeniOneri")}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-3"><Spinner size="sm" /></div>
        ) : items.length === 0 ? (
          <p className="text-muted small text-center py-3">{t("inspectionHierarchy.henuzOneriEklenmemis")}</p>
        ) : (
          <div className="table-responsive">
            <Table hover bordered={false} className="align-middle mb-0 small">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>{t("inspectionHierarchy.ad")}</th>
                  <th>{t("inspectionHierarchy.aciklama")}</th>
                  <th style={{ width: 90 }} className="text-center">{t("inspectionHierarchy.islemler")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const id   = item.inspection_suggestion_id;
                  const name = item.translated_name || item.inspection_suggestion_name;
                  return (
                    <tr key={id}>
                      <td className="text-muted">{id}</td>
                      <td className="fw-semibold">{name}</td>
                      <td className="text-muted">{item.inspection_suggestion_description || '—'}</td>
                      <td>
                        <div className="d-flex justify-content-center gap-1">
                          <Button color="soft-primary" size="sm"
                            onClick={() => setModal({ open: true, item })}>
                            <Edit2 size={13} />
                          </Button>
                          <Button color="soft-danger" size="sm"
                            onClick={() => setDelModal({ open: true, item })}>
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>

      <ItemModal
        key={`sug-item-${modal.open}`}
        open={modal.open} levelCfg={SUGGESTION_CFG} parentId={null}
        item={modal.item}
        onSave={() => { setModal({ open: false, item: null }); load(); }}
        onClose={() => setModal({ open: false, item: null })}
      />
      <DeleteModal
        open={delModal.open} levelCfg={SUGGESTION_CFG} item={delModal.item}
        onConfirm={() => { setDelModal({ open: false, item: null }); load(); }}
        onClose={() => setDelModal({ open: false, item: null })}
      />
    </Card>
  );
}

// ── Bölüm 24.4 — Torun Listesi Modalı ────────────────────────────────────────
// getDescendants(ancestorLevel, ancestorId, descendantLevel) servisini kullanır.
// Hiyerarşinin herhangi iki seviyesi arasındaki tüm torunları flat liste olarak gösterir.
function DescendantsModal({ open, levelCfg, item, onClose }) {
  const { t } = useTranslation("formlar");
  const ancestorIdx       = LEVEL_CONFIG.findIndex((c) => c.key === levelCfg?.key);
  const descendantOptions = LEVEL_CONFIG.slice(ancestorIdx + 1); // mevcut seviyenin altındakiler

  const [selectedDescLevel, setSelectedDescLevel] = useState('');
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Kapanınca state'i sıfırla — inner fn (ESLint: set-state-in-effect)
  useEffect(() => {
    function reset() {
      setSelectedDescLevel('');
      setData([]);
      setError(null);
    }
    if (!open) reset();
  }, [open]);

  // Seviye seçilince veriyi çek — inner async fn
  useEffect(() => {
    if (!selectedDescLevel || !item || !levelCfg) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const ancestorId = itemId(levelCfg, item);
        const result = await adminSvc.getDescendants(levelCfg.key, ancestorId, selectedDescLevel);
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) setError(i18n.t("formlar:inspectionHierarchy.torunlarYuklenemediLutfenTekrarDeneyin"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [selectedDescLevel, item, levelCfg]);

  const descCfg = LEVEL_CONFIG.find((c) => c.key === selectedDescLevel);

  return (
    <Modal isOpen={open} toggle={onClose} size="lg" scrollable centered>
      <ModalHeader toggle={onClose}>
        <ListTree size={16} className="me-2 text-muted" />
        {t("inspectionHierarchy.torunListele")}
        <span className="fw-normal text-muted">
          {item && levelCfg ? itemName(levelCfg, item) : ''}
        </span>
      </ModalHeader>
      <ModalBody>
        {/* Hedef seviye seçici */}
        <div className="mb-3">
          <label className="form-label small fw-semibold">{t("inspectionHierarchy.hangiSeviyedekiTorunlariGormekIstiyorsunuz")}</label>
          <Input
            type="select"
            value={selectedDescLevel}
            onChange={(e) => { setSelectedDescLevel(e.target.value); setData([]); setError(null); }}
          >
            <option value="">{t("inspectionHierarchy.seviyeSecin")}</option>
            {descendantOptions.map((c) => (
              <option key={c.key} value={c.key}>{c.plural}</option>
            ))}
          </Input>
          {selectedDescLevel && (
            <small className="text-muted">
              {t("inspectionHierarchy.secilenSeviye")}{" "}<strong>{descCfg?.plural}</strong>{" "}{t("inspectionHierarchy.tumTorunlarFlatListeOlarak")}
            </small>
          )}
        </div>

        {/* Sonuçlar */}
        {loading && (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        )}
        {error && <Alert color="danger">{error}</Alert>}
        {!loading && !error && selectedDescLevel && data.length === 0 && (
          <div className="text-center text-muted py-4">
            <ListTree size={36} className="mb-2 opacity-25" />
            <p className="mb-0">{t("inspectionHierarchy.buSeviyedeTorunBulunamadi")}</p>
          </div>
        )}
        {!loading && !error && data.length > 0 && (
          <div className="table-responsive">
            <Table hover size="sm" className="align-middle small mb-1">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>{t("inspectionHierarchy.ad")}</th>
                  {descCfg?.key === 'nonconformities' && (
                    <th className="text-center" style={{ width: 100 }}>{t("inspectionHierarchy.riskDegeri")}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => {
                  const rowId   = descCfg ? itemId(descCfg, row) : i;
                  const rowName = descCfg ? itemName(descCfg, row) : '—';
                  return (
                    <tr key={rowId ?? i}>
                      <td className="text-muted font-monospace small">{rowId}</td>
                      <td>{rowName}</td>
                      {descCfg?.key === 'nonconformities' && (
                        <td className="text-center">
                          {row.risk_degeri != null && (
                            <Badge color={
                              row.risk_degeri >= 200 ? 'danger'
                              : row.risk_degeri >= 70 ? 'warning'
                              : 'success'
                            }>
                              {row.risk_degeri}
                            </Badge>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <small className="text-muted">{t("inspectionHierarchy.toplam")}{" "}<strong>{data.length}</strong>{" "}{t("inspectionHierarchy.kayit")}</small>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>{t("common:kapat")}</Button>
      </ModalFooter>
    </Modal>
  );
}

// ── Ana Bileşen ───────────────────────────────────────────────────────────────

// Sadece bu iki seviyede reorder destekleniyor (backend kısıtı)
const REORDERABLE_LEVELS = new Set(['categories', 'questions']);

function InspectionHierarchyPage() {
  const { t } = useTranslation("formlar");
  const navigate = useNavigate();

  // path: [{ levelIdx, id, name }]  — drill-down geçmişi
  const [path,        setPath]        = useState([]);
  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [loadError,   setLoadError]   = useState(null);
  const [itemModal,   setItemModal]   = useState({ open: false, item: null });
  const [delModal,    setDelModal]    = useState({ open: false, item: null });
  const [copyModal,   setCopyModal]   = useState({ open: false, item: null }); // 24.5
  const [pullModal,   setPullModal]   = useState({ open: false });             // 2.2
  const [descModal,   setDescModal]   = useState({ open: false, item: null }); // 24.4
  const [departments, setDepartments] = useState([]);
  const [showAllScopes,  setShowAllScopes]  = useState(false);
  const [showAllHazards, setShowAllHazards] = useState(false);
  const [reordering,  setReordering]  = useState(false); // reorder API çağrısı sırasında

  const levelIdx   = path.length;
  const levelCfg   = LEVEL_CONFIG[levelIdx];
  const parentNode = path.length > 0 ? path[path.length - 1] : null;
  const parentId   = parentNode?.id ?? null;

  // Birim listesini bir kez yükle (kapsam modalı dropdown'ı için)
  useEffect(() => {
    adminSvc.listItems('departments', { languageId: 1 })
      .then(setDepartments)
      .catch(() => {});
  }, []);

  // Denetim Yeri seviyesinden çıkınca "Tüm Denetim Yerleri" modunu sıfırla
  useEffect(() => {
    async function run() {
      if (levelCfg?.key !== 'scopes')  setShowAllScopes(false);
      if (levelCfg?.key !== 'hazards') setShowAllHazards(false);
    }
    run();
  }, [levelCfg]);

  const loadItems = useCallback(async () => {
    if (!levelCfg) return;
    setLoading(true); setLoadError(null);
    try {
      // Tüm Uygunsuzluklar modunda parentId görmezden gel → GET /hazards (tümü — standalone dahil)
      const isHazardAll = levelCfg.key === 'hazards' && showAllHazards;
      // Tüm Denetim Yerleri modunda parentId görmezden gel
      const isScopeAll  = levelCfg.key === 'scopes'  && showAllScopes;
      const effectiveParentId = (isHazardAll || isScopeAll)
        ? undefined
        : (parentId ?? undefined);
      const data = await adminSvc.listItems(levelCfg.key, {
        parentId: effectiveParentId,
        languageId: 1,
      });
      setItems(data);
    } catch {
      setLoadError(i18n.t("formlar:inspectionHierarchy.verilerYuklenemediLutfenTekrarDeneyin"));
    } finally { setLoading(false); }
  }, [levelCfg, parentId, showAllScopes, showAllHazards]);

  useEffect(() => {
    document.title = i18n.t("formlar:inspectionHierarchy.kontrolVeUygunsuzlukFormuHiyerarsi");
    async function run() { await loadItems(); } run();
  }, [loadItems]);

  // ── Reorder yardımcıları ───────────────────────────────────────────────────
  const isReorderable = levelCfg && REORDERABLE_LEVELS.has(levelCfg.key);

  const idOf = useCallback((item) => itemId(levelCfg, item), [levelCfg]);

  const applyReorder = useCallback(async (newItems) => {
    setItems(newItems);
    setReordering(true);
    try {
      await adminSvc.reorderItems(levelCfg.key, newItems.map((it) => idOf(it)));
    } catch {
      // sessiz — gerekirse toast eklenebilir
    } finally {
      setReordering(false);
    }
  }, [levelCfg, idOf]);

  // ↑ Bir üste taşı
  const moveUp = useCallback((idx) => {
    if (idx === 0) return;
    applyReorder(arrayMove(items, idx, idx - 1));
  }, [items, applyReorder]);

  // ↓ Bir alta taşı
  const moveDown = useCallback((idx) => {
    if (idx === items.length - 1) return;
    applyReorder(arrayMove(items, idx, idx + 1));
  }, [items, applyReorder]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => String(idOf(it)) === String(active.id));
    const newIndex = items.findIndex((it) => String(idOf(it)) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    applyReorder(arrayMove(items, oldIndex, newIndex));
  }, [items, idOf, applyReorder]);

  function drillDown(item) {
    if (!levelCfg || levelCfg.isLeaf) return;
    const nextIdx = levelIdx + 1;
    if (nextIdx >= LEVEL_CONFIG.length) return;
    setPath((prev) => [
      ...prev,
      { levelIdx, id: itemId(levelCfg, item), name: itemName(levelCfg, item) },
    ]);
  }

  function goBack() { setPath((prev) => prev.slice(0, -1)); }
  function goToIdx(i) { setPath((prev) => prev.slice(0, i + 1)); }

  const isLeaf = !levelCfg || levelCfg.isLeaf || levelIdx >= LEVEL_CONFIG.length - 1;

  // Seviye renk badge'i
  const levelColors = ['success', 'primary', 'info', 'secondary', 'dark', 'warning', 'danger', 'danger'];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("inspectionHierarchy.kontrolVeUygunsuzlukFormuHiyerarsi2")} pageTitle={t("inspectionHierarchy.formYonetimi")} />

        <Card className="border-0 shadow-sm">
          <CardBody>

            {/* ── Seviye Göstergesi (yatay) ─────────────────────────────── */}
            <div className="d-flex align-items-center flex-wrap gap-1 mb-1 pb-3 border-bottom">
              {LEVEL_CONFIG.map((cfg, i) => (
                <React.Fragment key={cfg.key}>
                  <Badge
                    color={i === levelIdx ? cfg.color : 'light'}
                    className={`border cursor-pointer px-2 py-1 ${i === levelIdx ? '' : 'text-muted'}`}
                    style={{ cursor: i <= levelIdx ? 'pointer' : 'default', fontSize: '0.72rem' }}
                    onClick={() => i < levelIdx ? setPath((prev) => prev.slice(0, i)) : undefined}
                  >
                    {i + 1}. {cfg.label}
                  </Badge>
                  {i < LEVEL_CONFIG.length - 1 && (
                    <ChevronRight size={12} className="text-muted" />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* ── Breadcrumb yolu ──────────────────────────────────────── */}
            <div className="d-flex align-items-center flex-wrap gap-1 mt-3 mb-3">
              <button
                className={`btn btn-sm ${path.length === 0 ? 'btn-success' : 'btn-light border'}`}
                onClick={() => setPath([])}
              >
                <i className="bx bxs-home me-1" />{t("inspectionHierarchy.birimler")}
              </button>
              {path.map((seg, i) => (
                <React.Fragment key={`${seg.id ?? 'all'}-${i}`}>
                  <ChevronRight size={14} className="text-muted" />
                  <button
                    className={`btn btn-sm ${i === path.length - 1 ? `btn-${levelColors[i + 1] || 'primary'}` : 'btn-light border'}`}
                    onClick={() => goToIdx(i)}
                  >
                    {seg.name}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* ── Başlık + Ekle Butonu ─────────────────────────────────── */}
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center gap-2">
                {path.length > 0 && (
                  <button className="btn btn-sm btn-light border" onClick={goBack}>
                    <ChevronLeft size={14} />
                  </button>
                )}
                <h6 className="fw-semibold mb-0">
                  {levelCfg?.plural}
                  {parentNode && !showAllScopes && !showAllHazards && (
                    <span className="text-muted fw-normal ms-2 small">
                      — <span className="fw-semibold">{parentNode.name}</span>{" "}{t("inspectionHierarchy.altinda")}
                    </span>
                  )}
                  {(showAllScopes || showAllHazards) && (
                    <span className="text-muted fw-normal ms-2 small">{t("inspectionHierarchy.tumu")}</span>
                  )}
                </h6>
                {/* Tüm Denetim Yerleri toggle */}
                {levelCfg?.key === 'scopes' && (
                  <button
                    className={`btn btn-sm ${showAllScopes ? 'btn-warning' : 'btn-outline-secondary'}`}
                    style={{ fontSize: '0.72rem' }}
                    title={showAllScopes ? t("inspectionHierarchy.sadeceBuBiriminDenetimYerlerini") : t("inspectionHierarchy.tumDenetimYerleriniGosterBirimsiz")}
                    onClick={() => setShowAllScopes((v) => !v)}
                  >
                    {showAllScopes ? t("inspectionHierarchy.tumDenetimYerleri") : t("inspectionHierarchy.tumDenetimYerleri2")}
                  </button>
                )}
                {/* Tüm Uygunsuzluklar toggle — hazard seviyesinde */}
                {levelCfg?.key === 'hazards' && (
                  <button
                    className={`btn btn-sm ${showAllHazards ? 'btn-warning' : 'btn-outline-secondary'}`}
                    style={{ fontSize: '0.72rem' }}
                    title={showAllHazards ? t("inspectionHierarchy.sadeceBuSoruyaBagliUygunsuzluklari") : t("inspectionHierarchy.tumUygunsuzluklariGosterSorusuzBagimsiz")}
                    onClick={() => setShowAllHazards((v) => !v)}
                  >
                    {showAllHazards ? t("inspectionHierarchy.tumUygunsuzluklar") : t("inspectionHierarchy.tumUygunsuzluklar2")}
                  </button>
                )}
                {/* Birimler seviyesinde → Tüm Denetim Yerleri kısayolu */}
                {levelCfg?.key === 'departments' && (
                  <button
                    className="btn btn-sm btn-outline-primary"
                    style={{ fontSize: '0.72rem' }}
                    title={t("inspectionHierarchy.birimSecmedenTumDenetimYerlerini")}
                    onClick={() => {
                      setShowAllScopes(true);
                      setPath([{ levelIdx: 0, id: null, name: t("inspectionHierarchy.tumDenetimYerleri2") }]);
                    }}
                  >
                    {t("inspectionHierarchy.tumDenetimYerleri3")}
                  </button>
                )}
              </div>
              <div className="d-flex gap-2">
                {levelCfg && !NON_COPYABLE.has(levelCfg.key) && (
                  <Button
                    color="soft-warning" size="sm"
                    className="d-flex align-items-center gap-1"
                    title={t("inspectionHierarchy.baskaBirKonumdakiOgeyiKopyalayarak")}
                    onClick={() => setPullModal({ open: true })}
                  >
                    <Copy size={13} />{" "}{t("inspectionHierarchy.baskaYerdenCek2")}
                  </Button>
                )}
                <Button
                  color="primary" size="sm"
                  className="d-flex align-items-center gap-1"
                  onClick={() => setItemModal({ open: true, item: null })}
                >
                  <Plus size={13} />{" "}{t("inspectionHierarchy.yeni", { label: levelCfg?.label })}
                </Button>
              </div>
            </div>

            {/* ── Liste ────────────────────────────────────────────────── */}
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : loadError ? (
              <Alert color="danger" className="d-flex align-items-center gap-2">
                <i className="bx bx-error-circle" /> {loadError}
              </Alert>
            ) : items.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bx bx-folder-open fs-1 d-block mb-2 opacity-25" />
                <p className="mb-3">
                  {parentNode
                    ? t("inspectionHierarchy.altindaHenuzYok", { name: parentNode.name, v: levelCfg?.label?.toLowerCase() })
                    : t("inspectionHierarchy.henuzEklenmemis", { v: levelCfg?.label?.toLowerCase() })}
                </p>
                <Button color="primary" size="sm"
                  onClick={() => setItemModal({ open: true, item: null })}>
                  <Plus size={13} className="me-1" />{t("inspectionHierarchy.ilkIEkle", { label: levelCfg?.label })}
                </Button>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={isReorderable ? handleDragEnd : undefined}
              >
              <div className="table-responsive">
                <Table hover bordered={false} className="align-middle mb-0 small">
                  <thead className="table-light">
                    <tr>
                      {isReorderable && (
                        <th style={{ width: 36 }} className="text-center" title={t("inspectionHierarchy.surukleVeyaIleSirala")}>
                          {reordering ? <Spinner size="sm" /> : <GripVertical size={13} className="text-muted" />}
                        </th>
                      )}
                      <th style={{ width: 60 }}>#</th>
                      <th>{t("inspectionHierarchy.ad")}</th>
                      {levelCfg?.key === 'scopes'           && <th>{t("inspectionHierarchy.hedefVarlik")}</th>}
                      {levelCfg?.key === 'types'            && <th>{t("inspectionHierarchy.kisaKod")}</th>}
                      {levelCfg?.key === 'nonconformities'  && <th className="text-center">{t("inspectionHierarchy.riskDegeri")}</th>}
                      <th style={{ width: 190 }} className="text-center">{t("inspectionHierarchy.islemler")}</th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={isReorderable ? items.map((it) => String(idOf(it))) : []}
                    strategy={verticalListSortingStrategy}
                  >
                  <tbody>
                    {items.map((item, idx) => {
                      const id   = itemId(levelCfg, item);
                      const name = itemName(levelCfg, item);
                      return (
                        <SortableRow
                          key={id}
                          sortId={String(id)}
                          isReorderable={isReorderable}
                          disabled={reordering}
                        >
                          {/* ↑↓ yön butonları */}
                          {isReorderable && (
                            <td className="text-center p-1" style={{ width: 36 }}>
                              <div className="d-flex flex-column align-items-center gap-0" style={{ gap: 1 }}>
                                <button
                                  className="btn btn-sm p-0 text-muted"
                                  style={{ lineHeight: 1, fontSize: '0.65rem' }}
                                  title={t("inspectionHierarchy.yukariTasi")}
                                  disabled={idx === 0 || reordering}
                                  onClick={() => moveUp(idx)}
                                >
                                  <ChevronUp size={11} />
                                </button>
                                <button
                                  className="btn btn-sm p-0 text-muted"
                                  style={{ lineHeight: 1, fontSize: '0.65rem' }}
                                  title={t("inspectionHierarchy.asagiTasi")}
                                  disabled={idx === items.length - 1 || reordering}
                                  onClick={() => moveDown(idx)}
                                >
                                  <ChevronDown size={11} />
                                </button>
                              </div>
                            </td>
                          )}
                          <td className="text-muted">{id}</td>
                          <td>
                            <span className="fw-semibold">{name}</span>
                            {/* Standalone hazard badge — parentId null olanlar */}
                            {levelCfg.key === 'hazards' && item.inspection_question_id == null && (
                              <Badge color="secondary" className="ms-1" style={{ fontSize: '0.65rem' }}>{t("inspectionHierarchy.bagimsiz")}</Badge>
                            )}
                            {item.inspection_scope_description && (
                              <div className="text-muted opacity-75" style={{ fontSize: '0.7rem' }}>
                                {item.inspection_scope_description}
                              </div>
                            )}
                            {item.main_question && (
                              <div className="text-muted opacity-75" style={{ fontSize: '0.7rem' }}>
                                {item.main_question}
                              </div>
                            )}
                          </td>

                          {levelCfg.key === 'scopes' && (
                            <td>
                              {item.target_entity && (
                                <Badge color="light" className="border text-dark">
                                  {item.target_entity}
                                </Badge>
                              )}
                            </td>
                          )}
                          {levelCfg.key === 'types' && (
                            <td>
                              {item.inspection_type_short && (
                                <Badge color="light" className="border text-dark">
                                  {item.inspection_type_short}
                                </Badge>
                              )}
                            </td>
                          )}
                          {levelCfg.key === 'nonconformities' && (
                            <td className="text-center">
                              {item.risk_degeri != null && (
                                <Badge
                                  color={item.risk_degeri >= 40 ? 'danger' : item.risk_degeri >= 20 ? 'warning' : 'success'}
                                  pill
                                >
                                  {item.risk_degeri}
                                </Badge>
                              )}
                            </td>
                          )}

                          <td>
                            <div className="d-flex justify-content-center gap-1">
                              {/* Toplu Fotoğraf Modu — sadece kategoriler */}
                              {levelCfg.key === 'categories' && (
                                <BulkPhotoModeButton categoryId={id} categoryName={name} />
                              )}
                              {/* FormBuilder bağlantısı — sadece types */}
                              {levelCfg.hasFormBuilder && (
                                <Button color="soft-info" size="sm"
                                  title={t("inspectionHierarchy.formBuilderDaAcKategoriSoru")}
                                  onClick={() => navigate(`/Management/Forms/${id}/edit`)}>
                                  <ExternalLink size={13} />
                                </Button>
                              )}
                              {/* Drill-down — leaf değilse */}
                              {!isLeaf && (
                                <Button
                                  color={item.child_count === 0 ? 'light' : 'soft-secondary'}
                                  size="sm"
                                  title={item.child_count === 0
                                    ? t("inspectionHierarchy.altYok", { v: LEVEL_CONFIG[levelIdx + 1]?.plural || t("inspectionHierarchy.oge2") })
                                    : t("inspectionHierarchy.gor", { child_count: item.child_count, v: LEVEL_CONFIG[levelIdx + 1]?.label || t("inspectionHierarchy.oge2_2") })}
                                  onClick={() => drillDown(item)}
                                  className="d-flex align-items-center gap-1"
                                >
                                  <ChevronRight size={13} className={item.child_count === 0 ? 'text-muted' : ''} />
                                  {item.child_count > 0 && (
                                    <span style={{ fontSize: '0.7rem', lineHeight: 1 }}>{item.child_count}</span>
                                  )}
                                </Button>
                              )}
                              {/* Düzenle */}
                              <Button color="soft-primary" size="sm" title={t("common:duzenle")}
                                onClick={() => setItemModal({ open: true, item })}>
                                <Edit2 size={13} />
                              </Button>
                              {/* Kopyala — suggestions ve legal-procedures kopyalanamaz */}
                              {!NON_COPYABLE.has(levelCfg.key) && (
                                <Button color="soft-warning" size="sm"
                                  title={t("inspectionHierarchy.altAgaciylaKopyalaGeriAlinamaz")}
                                  onClick={() => setCopyModal({ open: true, item })}>
                                  <Copy size={13} />
                                </Button>
                              )}
                              {/* Torun Listele — Bölüm 24.4: leaf olmayan tüm seviyelerde */}
                              {!isLeaf && (
                                <Button color="soft-info" size="sm"
                                  title={t("inspectionHierarchy.torunlariFlatListeOlarakGoruntule")}
                                  onClick={() => setDescModal({ open: true, item })}>
                                  <ListTree size={13} />
                                </Button>
                              )}
                              {/* Sil */}
                              <Button color="soft-danger" size="sm" title={t("inspectionHierarchy.softSil")}
                                onClick={() => setDelModal({ open: true, item })}>
                                <Trash2 size={13} />
                              </Button>
                            </div>
                          </td>
                        </SortableRow>
                      );
                    })}
                  </tbody>
                  </SortableContext>
                </Table>
              </div>
              </DndContext>
            )}
          </CardBody>
        </Card>

        {/* Yasal Prosedürler */}
        <LegalProceduresCard />

        {/* Öneriler — Bölüm 24.1 */}
        <SuggestionsCard />
      </Container>

      {/* Modaller */}
      <ItemModal
        key={`item-${levelIdx}-${itemModal.open}`}
        open={itemModal.open}
        levelCfg={levelCfg}
        parentId={parentId}
        item={itemModal.item}
        hazardId={levelCfg?.key === 'nonconformities' ? path[5]?.id ?? null : null}
        optionsMap={levelCfg?.key === 'scopes' ? {
          parentId: [
            { value: '', label: t("inspectionHierarchy.birimsiz") },
            ...departments.map((d) => ({
              value: d.inspection_department_id,
              label: d.translated_name || d.inspection_department_name,
            })),
          ],
        } : {}}
        onSave={() => { setItemModal({ open: false, item: null }); loadItems(); }}
        onClose={() => setItemModal({ open: false, item: null })}
      />
      <DeleteModal
        open={delModal.open}
        levelCfg={levelCfg}
        item={delModal.item}
        onConfirm={() => { setDelModal({ open: false, item: null }); loadItems(); }}
        onClose={() => setDelModal({ open: false, item: null })}
      />
      {/* Copy Subtree Modal — Bölüm 24.5 / 2.1 */}
      <CopyModal
        key={`copy-${levelCfg?.key ?? 'none'}-${copyModal.item ? itemId(levelCfg, copyModal.item) : 'empty'}`}
        open={copyModal.open}
        levelCfg={levelCfg}
        item={copyModal.item}
        onClose={() => setCopyModal({ open: false, item: null })}
        onSuccess={(count, newId) => {
          alert(t("inspectionHierarchy.kopyalandiKayitKopyalandiYeniRoot", { count, newId }));
          loadItems();
          setCopyModal({ open: false, item: null });
        }}
      />
      {/* Başka Yerden Çek Modal — Bölüm 2.2 */}
      <PullModal
        key={`pull-${levelCfg?.key ?? 'none'}-${pullModal.open}`}
        open={pullModal.open}
        levelCfg={levelCfg}
        targetParentId={parentId}
        onClose={() => setPullModal({ open: false })}
        onSuccess={(count, newId) => {
          alert(t("inspectionHierarchy.kopyalandiKayitKopyalandiYeniId", { count, newId }));
          loadItems();
          setPullModal({ open: false });
        }}
      />

      {/* Torun Listele Modal — Bölüm 24.4 */}
      <DescendantsModal
        key={`desc-${levelCfg?.key ?? 'none'}-${descModal.item ? itemId(levelCfg, descModal.item) : 'empty'}`}
        open={descModal.open}
        levelCfg={levelCfg}
        item={descModal.item}
        onClose={() => setDescModal({ open: false, item: null })}
      />
    </div>
  );
}

export default InspectionHierarchyPage;

// ── SortableRow — @dnd-kit ile sürüklenebilir tablo satırı ───────────────────
// isReorderable=false ise normal <tr> gibi davranır.
function SortableRow({ sortId, isReorderable, disabled, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortId, disabled: !isReorderable || disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#f0f4ff' : undefined,
    cursor: isReorderable ? (isDragging ? 'grabbing' : 'grab') : undefined,
  };

  // drag handle'ı ilk <td> olarak alıyoruz — listeners/attributes oraya
  // children = [handleTd, ...diğer td'ler]
  const childArray = React.Children.toArray(children);

  if (!isReorderable) {
    return <tr ref={setNodeRef} style={style}>{children}</tr>;
  }

  return (
    <tr ref={setNodeRef} style={style}>
      {childArray.map((child, i) =>
        i === 0
          ? React.cloneElement(child, { ...attributes, ...listeners })
          : child
      )}
    </tr>
  );
}

